import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateTimeRemaining, CountdownData } from '@/lib/index';
const springTransition = {
  type: "spring",
  stiffness: 400,
  damping: 30
};

/**
 * Sub-componente para mostrar una unidad de tiempo individual (Días, Horas, etc.)
 * con una animación fluida al cambiar el valor y tipografía técnica.
 */
function TimeUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="group relative flex flex-col items-center justify-center p-2 sm:p-4 md:p-5 min-w-[60px] sm:min-w-[100px] md:min-w-[130px] bg-card border border-border rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 hover:border-accent/40 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
      <div className="relative h-8 sm:h-12 md:h-16 overflow-hidden flex items-center justify-center w-full">
        <AnimatePresence mode="popLayout">
          <motion.span key={value} initial={{
          y: 20,
          opacity: 0,
          filter: "blur(2px)"
        }} animate={{
          y: 0,
          opacity: 1,
          filter: "blur(0px)"
        }} exit={{
          y: -20,
          opacity: 0,
          filter: "blur(2px)"
        }} transition={springTransition} className="text-xl sm:text-3xl md:text-5xl lg:text-6xl font-mono font-extrabold text-foreground tabular-nums tracking-tighter">
            {value.toString().padStart(2, '0')}
          </motion.span>
        </AnimatePresence>
      </div>
      
      <div className="mt-1 sm:mt-2 md:mt-3 flex flex-col items-center">
        <div className="h-0.5 w-3 sm:w-4 md:w-6 bg-accent rounded-full mb-1 sm:mb-1.5 md:mb-2 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
        <span className="text-[8px] sm:text-[10px] md:text-xs font-bold uppercase tracking-[0.1em] sm:tracking-[0.2em] md:tracking-[0.25em] text-muted-foreground group-hover:text-foreground transition-colors duration-300">
          {label}
        </span>
      </div>

      {/* Sutil brillo decorativo */}
      <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-accent/5 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>;
}

/**
 * Componente de contador regresivo para el evento Ibiza Bike Epic 2026.
 * Presenta una estética minimalista, técnica y animaciones de alto impacto.
 */
export function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState<CountdownData>(calculateTimeRemaining());
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeRemaining());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  if (timeLeft.isExpired) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="inline-flex items-center gap-4 px-10 py-5 bg-primary text-primary-foreground rounded-full font-bold text-xl uppercase tracking-widest shadow-2xl shadow-primary/20 border border-white/10">
        <span className="relative flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-accent"></span>
        </span>
        ¡La Épica ha Comenzado!
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.8,
    ease: "easeOut"
  }} className="w-full flex flex-col items-center justify-center">
      <div className="flex justify-center items-center gap-1 sm:gap-3 md:gap-4 lg:gap-5 w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto">
        <TimeUnit value={timeLeft.days} label="Días" />
        <TimeUnit value={timeLeft.hours} label="Horas" />
        <TimeUnit value={timeLeft.minutes} label="Minutos" />
        <TimeUnit value={timeLeft.seconds} label="Segundos" />
      </div>
    </motion.div>;
}